import React, { useState, useEffect } from 'react'
import {
  Box,
  Flex,
  Heading,
  Text,
  Input,
  Button,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react'
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from 'firebase/firestore'
import { db } from '../config/firebase'

const SITES_COLLECTION = 'sites'

interface Site {
  id: string
  name?: string
  url?: string
  order?: number
}

const PromoSitesManager = () => {
  const [sites, setSites] = useState<Site[]>([])
  const [loading, setLoading] = useState(true)
  const [newName, setNewName] = useState('')
  const [newUrl, setNewUrl] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingName, setEditingName] = useState('')
  const [editingUrl, setEditingUrl] = useState('')
  const [error, setError] = useState('')
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const fetchSites = async () => {
    if (!db) {
      setError('Firebase가 설정되지 않았습니다.')
      setLoading(false)
      return
    }
    try {
      const snapshot = await getDocs(collection(db, SITES_COLLECTION))
      const list = snapshot.docs
        .map((d) => ({
          id: d.id,
          ...d.data(),
          order: d.data().order ?? 999999,
        }))
        .sort((a, b) => (a.order ?? 999999) - (b.order ?? 999999)) as Site[]
      setSites(list)
    } catch (err) {
      setError('목록을 불러오는데 실패했습니다: ' + (err as Error).message)
    } finally {
      setLoading(false)
    }
  }

  const addSite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!newName.trim()) {
      setError('사이트명을 입력해주세요')
      return
    }
    if (!newUrl.trim()) {
      setError('URL을 입력해주세요')
      return
    }
    if (!db) return
    try {
      const maxOrder =
        sites.length > 0 ? Math.max(...sites.map((s) => s.order ?? 0)) : -1
      await addDoc(collection(db, SITES_COLLECTION), {
        name: newName.trim(),
        url: newUrl.trim(),
        order: maxOrder + 1,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      setNewName('')
      setNewUrl('')
      setError('')
      await fetchSites()
    } catch (err) {
      setError('추가에 실패했습니다: ' + (err as Error).message)
    }
  }

  const updateSite = async (id: string) => {
    if (!editingName.trim() || !editingUrl.trim()) {
      setError('사이트명과 URL을 입력해주세요')
      return
    }
    if (!db) return
    try {
      await updateDoc(doc(db, SITES_COLLECTION, id), {
        name: editingName.trim(),
        url: editingUrl.trim(),
        updatedAt: serverTimestamp(),
      })
      setEditingId(null)
      setEditingName('')
      setEditingUrl('')
      setError('')
      await fetchSites()
    } catch (err) {
      setError('수정에 실패했습니다: ' + (err as Error).message)
    }
  }

  const deleteSite = async (id: string) => {
    if (!window.confirm('정말로 삭제하시겠습니까?')) return
    if (!db) return
    try {
      await deleteDoc(doc(db, SITES_COLLECTION, id))
      await fetchSites()
    } catch (err) {
      setError('삭제에 실패했습니다: ' + (err as Error).message)
    }
  }

  const copyLink = async (site: Site) => {
    const linkUrl = `${window.location.origin}/links`
    try {
      await navigator.clipboard.writeText(linkUrl)
      setCopiedId(site.id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch {
      const ta = document.createElement('textarea')
      ta.value = linkUrl
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
      setCopiedId(site.id)
      setTimeout(() => setCopiedId(null), 2000)
    }
  }

  const startEdit = (site: Site) => {
    setEditingId(site.id)
    setEditingName(site.name ?? '')
    setEditingUrl(site.url ?? '')
  }

  const cancelEdit = () => {
    setEditingId(null)
    setEditingName('')
    setEditingUrl('')
  }

  useEffect(() => {
    fetchSites()
  }, [])

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Text color="gray.600">목록을 불러오는 중...</Text>
      </Box>
    )
  }

  return (
    <Box p={{ base: 4, sm: 8 }}>
      <Heading as="h2" size="lg" color="gray.900" mb={8}>
        홍보페이지 관리
      </Heading>

      <Box
        as="form"
        onSubmit={addSite}
        bg="gray.50"
        borderRadius="lg"
        p={6}
        mb={8}
      >
        <Flex flexDirection={{ base: 'column', sm: 'row' }} gap={4}>
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="사이트명"
            w={{ base: '100%', sm: '256px' }}
            borderColor="gray.300"
            _focus={{
              borderColor: 'blue.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
            }}
          />
          <Input
            type="url"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            placeholder="URL (예: https://example.com)"
            flex={1}
            borderColor="gray.300"
            _focus={{
              borderColor: 'blue.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-blue-500)',
            }}
          />
          <Button
            type="submit"
            colorScheme="green"
            px={6}
            py={3}
            whiteSpace="nowrap"
          >
            추가
          </Button>
        </Flex>
      </Box>

      {error && (
        <Alert status="error" borderRadius="lg" mb={6}>
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Box>
        {sites.length === 0 ? (
          <Box textAlign="center" py={12}>
            <Text color="gray.600" fontSize="lg" mb={2}>
              등록된 홍보페이지가 없습니다.
            </Text>
            <Text color="gray.500">위에서 사이트를 추가해보세요.</Text>
          </Box>
        ) : (
          sites.map((site, index) => (
            <Box
              key={site.id}
              bg="white"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="lg"
              p={6}
              mb={4}
              _hover={{ boxShadow: 'md' }}
              transition="box-shadow 0.2s"
            >
              <Flex
                flexDirection={{ base: 'column', lg: 'row' }}
                alignItems={{ base: 'stretch', lg: 'center' }}
                justifyContent="space-between"
                gap={4}
              >
                <Flex alignItems="center" gap={4} flex={1} minW={0}>
                  <Flex
                    w={12}
                    h={12}
                    bg="blue.600"
                    color="white"
                    borderRadius="full"
                    alignItems="center"
                    justifyContent="center"
                    fontWeight="bold"
                    fontSize="lg"
                    flexShrink={0}
                  >
                    #{index + 1}
                  </Flex>
                  <Box flex={1} minW={0}>
                    {editingId === site.id ? (
                      <Flex flexDirection="column" gap={2}>
                        <Input
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          placeholder="사이트명"
                          size="sm"
                          borderColor="gray.300"
                        />
                        <Input
                          type="url"
                          value={editingUrl}
                          onChange={(e) => setEditingUrl(e.target.value)}
                          placeholder="URL"
                          size="sm"
                          borderColor="gray.300"
                        />
                      </Flex>
                    ) : (
                      <>
                        <Text fontWeight="semibold" color="gray.900" mb={1}>
                          {site.name ?? '이름 없음'}
                        </Text>
                        <Text
                          fontSize="sm"
                          color="gray.600"
                          wordBreak="break-all"
                        >
                          {site.url}
                        </Text>
                      </>
                    )}
                  </Box>
                </Flex>

                <Flex flexWrap="wrap" gap={2}>
                  {editingId === site.id ? (
                    <>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        onClick={() => updateSite(site.id)}
                      >
                        저장
                      </Button>
                      <Button size="sm" colorScheme="gray" onClick={cancelEdit}>
                        취소
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        colorScheme="green"
                        onClick={() => copyLink(site)}
                      >
                        {copiedId === site.id ? '복사됨!' : '공유'}
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        onClick={() => startEdit(site)}
                      >
                        수정
                      </Button>
                      <Button
                        size="sm"
                        colorScheme="red"
                        onClick={() => deleteSite(site.id)}
                      >
                        삭제
                      </Button>
                    </>
                  )}
                </Flex>
              </Flex>
            </Box>
          ))
        )}
      </Box>
    </Box>
  )
}

export default PromoSitesManager
